import { parseISO } from 'date-fns';
import * as A from 'fp-ts/Array';
import * as O from 'fp-ts/Option';
import * as React from 'react';
import { TrendingUp, FastForward } from 'react-feather';
import { pipe } from 'fp-ts/lib/function';
import { ButtonLink } from '../../components/Button';
import { Card } from '../../components/Card';
import { TextButton, TextLink } from '../../components/ClickableText';
import { Container, EdgeContainer } from '../../components/Container';
import { Heading } from '../../components/Heading';
import { Heatmap } from '../../components/Heatmap';
import { SharePageButton } from '../../components/SharePageButton';
import { sequenceTOption } from '../../function';
import { MatchActivityQuery } from '../../generated/graphql';
import { IconFire } from '../../icons/FireIcon';
import { IconWalking } from '../../icons/IconWalking';
import { MainLayout } from '../../layouts/MainLayout';
import { paths } from '../../paths';
import { toFixedDecimal } from '../../utils/numberFormat';
import { withIntendedPath } from '../../utils/withIntendedPath';
import { PlayerNameWithAvatar } from '../Match/components/PlayerNameWithAvatar';
import { isNil } from '../../utils/isNil';
import { formatDistance } from '../../utils/formatDistance';
import { useAuth } from '../../contexts/AuthContext';
import { useShareLink } from '../../hooks/useShareLink';

interface Props {
  activity: NonNullable<MatchActivityQuery['matchActivity']>;
}

export const ActivityHome: React.FC<Props> = ({ activity }) => {
  const { user } = useAuth();

  const { host, protocol } = window.location;
  const shareLink = `${protocol}//${host}${paths.activities.home(activity.id)}`;
  const heading = pipe(
    sequenceTOption(
      O.fromNullable(activity.player),
      O.fromNullable(activity.match),
    ),
    O.fold(
      () => 'Activity',
      ([player, match]) => `${player.name}'s match vs ${match.opponent}`,
    ),
  );

  const shareProps = {
    url: shareLink,
    shareText: `${heading} on Matchhaus`,
    context: 'match_activity',
  };
  const handleShare = useShareLink(shareProps);

  return (
    <MainLayout
      header={
        <div className="bg-primary text-white -mt-6 py-6">
          <Container className="flex justify-between">
            <Heading level={1} className="text-4xl">
              {heading}
            </Heading>
            <SharePageButton {...shareProps} title="Share activity" />
          </Container>
        </div>
      }
    >
      <EdgeContainer className="space-y-6 bg-grey-50 p-6 -my-6">
        <div className="flex justify-between items-center">
          <h2 className="font-semibold">Player Analysis</h2>
          {activity.player ? (
            <PlayerNameWithAvatar player={activity.player} />
          ) : null}
        </div>

        {activity.positions && A.isNonEmpty(activity.positions) ? (
          <Heatmap
            points={activity.positions.map((p) => ({
              ...p,
              timestamp: parseISO(p.t),
            }))}
          />
        ) : null}

        {isNil(user) ? (
          <div className="text-center space-y-4">
            <p>
              Want to see your own match performance?{' '}
              <TextLink to={paths.register()}>
                Create a Matchhaus account
              </TextLink>{' '}
              and get started straight away!
            </p>
            <p>
              Already got an account?{' '}
              <TextLink
                to={withIntendedPath(
                  paths.signIn(),
                  paths.activities.home(activity.id),
                )}
              >
                Sign in
              </TextLink>
            </p>
          </div>
        ) : null}

        {!isNil(activity.summaryData.distance) ? (
          <Card className="flex items-center">
            <TrendingUp className="text-green-700 mr-6" />
            <span className="mr-6">Distance</span>
            <span className="ml-auto text-xl ">
              {formatDistance(activity.summaryData.distance)}km
            </span>
          </Card>
        ) : null}

        {activity.summaryData.steps ? (
          <Card className="flex items-center">
            <IconWalking className="text-amber-700 mr-6" />
            <span className="mr-6">Steps</span>
            <span className="ml-auto text-xl">
              {toFixedDecimal(0)(activity.summaryData.steps)}
            </span>
          </Card>
        ) : null}

        {activity.summaryData.calories ? (
          <Card className="flex items-center">
            <IconFire className="text-red-700 mr-6" />
            <span className="mr-6">Calories</span>
            <span className="ml-auto text-xl ">
              {toFixedDecimal(0)(activity.summaryData.calories)}
            </span>
          </Card>
        ) : null}
        {activity.summaryData.maxSpeed ? (
          <Card className="flex items-center">
            <FastForward className="text-primary-700 mr-6" />
            <span className="mr-6">Max Speed</span>
            <span className="ml-auto text-xl ">
              {toFixedDecimal(2)(activity.summaryData.maxSpeed)}m/s
            </span>
          </Card>
        ) : null}

        {activity.match ? (
          <ButtonLink to={paths.match.home(activity.match.id)} kind="plain">
            Go to match
          </ButtonLink>
        ) : null}

        <TextButton
          onClick={handleShare}
          className="w-full block text-center p-3"
        >
          Share match activity
        </TextButton>
      </EdgeContainer>
    </MainLayout>
  );
};
